import React, { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import PersonIcon from "@mui/icons-material/Person";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChatIcon from "@mui/icons-material/Chat";
import { Fab } from "@mui/material";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import ToolSelector from "./components/ToolSelector";
import Assets from "./components/Assets";
import Maintenance from "./components/Maintenance";
import Modeling from "./components/Modeling";
import { DashboardV2 } from "./components/dashboardV2/DashboardV2";
import { Inspection } from "./components/Inspection/Inspection";

// Factory locations and tool types
const factoryLocations = ["Surat", "Ahmedabad"];

const toolTypes = ["Solar Panels", "Inverters"];

const etchingTools = [
  "Monocrystalline",
  "Polycrystalline",
  "N-type Topcon",
  "Bifacial",
];

function App() {
  const [activeTab, setActiveTab] = useState(0);
  const [location, setLocation] = useState(factoryLocations[0]);
  const [toolType, setToolType] = useState(toolTypes[0]);
  const [selectedTool, setSelectedTool] = useState(etchingTools[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [currentTime, setCurrentTime] = useState(() =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
  const [mode, setMode] = useState<"light" | "dark">("light");
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isCoPilotOpen, setIsCoPilotOpen] = useState(false);

  const theme = createTheme({
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            background: {
              default: "#181C23", // main background
              paper: "#232837", // sidebar/paper
            },
            primary: {
              main: "#00adef", // Samba blue
            },
            secondary: {
              main: "#00eaff", // cyan accent
            },
            text: {
              primary: "#fff",
              secondary: "#b0b8c1",
            },
            divider: "rgba(0, 174, 239, 0.12)",
          }
        : {
            background: {
              default: "#fafaff",
              paper: "#fff",
            },
            primary: {
              main: "#00adef",
            },
            secondary: {
              main: "#800000",
            },
            text: {
              primary: "#18181b",
              secondary: "#6e6e73",
            },
            divider: "rgba(124,58,237,0.08)",
          }),
    },
    typography: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      h4: { fontWeight: 800, letterSpacing: "-0.02em" },
      h6: { fontWeight: 700 },
      body1: { fontWeight: 500 },
    },
    shape: {
      borderRadius: 20,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            boxShadow: "0 4px 24px 0 rgba(0,174,239,0.08)",
            backgroundColor: mode === "dark" ? "#232837" : "#fff",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
          },
        },
      },
    },
  });

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    // Add logout logic here
  };

  const handleOpenHelp = () => setIsHelpOpen(true);
  const handleCloseHelp = () => setIsHelpOpen(false);

  const handleToggleCoPilot = () => setIsCoPilotOpen((prev) => !prev);
  const handleCloseCoPilot = () => setIsCoPilotOpen(false);

  const handleSettingsClick = () => {
    // Add settings logic here
  };

  const renderMainContent = () => {
    switch (activeTab) {
      case 0:
        return <DashboardV2 />;
      case 1:
        return <Inspection />;
      case 2:
        return <Maintenance />;
      case 3:
        return <Modeling />;
      default:
        return (
          <Dashboard
            location={location}
            toolType={toolType}
            selectedTool={selectedTool}
          />
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        {/* Sidebar */}
        <Sidebar
          activeIndex={activeTab}
          onSelect={setActiveTab}
          onSettingsClick={handleSettingsClick}
        />

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, ml: "72px" }}>
          {/* Top Bar */}
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1000,
              borderRadius: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
                minHeight: 70,
              }}
            >
              {/* Left side - Title and selectors */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: "2rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <span style={{ color: "#00adef" }}>Via</span>
                  <span style={{ color: "#4caf50" }}> Connect</span>
                </Typography>

                <ToolSelector
                  location={location}
                  setLocation={setLocation}
                  toolType={toolType}
                  setToolType={setToolType}
                  selectedTool={selectedTool}
                  setSelectedTool={setSelectedTool}
                  factoryLocations={factoryLocations}
                  toolTypes={toolTypes}
                  etchingTools={etchingTools}
                />
              </Box>

              {/* Right side - User controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  onClick={() => setMode(mode === "light" ? "dark" : "light")}
                >
                  {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                </IconButton>

                <IconButton onClick={handleOpenHelp}>
                  <HelpOutlineIcon />
                </IconButton>

                <IconButton onClick={handleToggleCoPilot}>
                  <ChatIcon />
                </IconButton>

                <IconButton onClick={handleUserMenu}>
                  <Avatar
                    sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                  >
                    <PersonIcon />
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    sx: { borderRadius: 3, mt: 1 },
                  }}
                >
                  <MenuItem onClick={handleClose}>Profile</MenuItem>
                  <MenuItem onClick={handleClose}>Settings</MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </Box>
            </Box>
          </Paper>

          {/* Main Content Area */}
          <Container maxWidth={false} sx={{ py: 3, px: 3 }}>
            {renderMainContent()}
          </Container>
        </Box>

        {/* CoPilot Floating Button */}
        {isCoPilotOpen && (
          <Fab
            color="primary"
            aria-label="CoPilot"
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
            onClick={handleCloseCoPilot}
          >
            <ChatIcon />
          </Fab>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;
